import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Observable, ReplaySubject } from 'rxjs';
import { map, tap, catchError } from 'rxjs/operators';
import { ApiResponse } from '../../shared/models/api-response.model';
import { LogoFavicon } from '../../shared/models/logo-favicon.model';

@Injectable({
  providedIn: 'root'
})
export class LogoFaviconHttpService {
  desktop_logo$ = new ReplaySubject<string>(1);

  // Inject PLATFORM_ID to detect browser vs server
  constructor(
    private http: HttpClient,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {}

  getLogoFavicon(): Observable<LogoFavicon> {
    return this.http.get<ApiResponse>('/logofavicon/getlogofavicon').pipe(
      map(res => res.data)
    );
  }

  updateLogoAndFavicon(): void {
    this.getLogoFavicon().pipe(
      tap(res => {
        // Only run DOM/sessionStorage logic in browser
        if (isPlatformBrowser(this.platformId)) {

          sessionStorage.setItem('logo_favicon', JSON.stringify(res));

          window.dispatchEvent(new CustomEvent('logo-favicon-set', {
            detail: { key: 'logo_favicon', value: res }
          }));

          this.setFaviconLink(res);
          this.setLogoLink(res);
        }

        // Always emit logo URL (safe, no DOM)
        this.setLogoObservable(res);
      }),
      catchError(error => {
        if (isPlatformBrowser(this.platformId)) {
          this.setFaviconLink();   // default favicon
          this.setLogoLink();      // default logo from CSS var
        }
        this.setLogoObservable(); // fallback
        throw error;
      })
    ).subscribe();
  }

  private setFaviconLink(logofavicon?: LogoFavicon): void {
    if (!isPlatformBrowser(this.platformId)) return;

    let link = 'favicon.ico';
    if (logofavicon && (logofavicon.seasonal_favicon || logofavicon.favicon)) {
      link = (logofavicon.seasonal_favicon || logofavicon.favicon).toString();
    }

    const faviconElement = document.getElementById('favicon') as HTMLLinkElement;
    if (faviconElement) {
      faviconElement.href = link;
    }
  }

  private setLogoLink(logofavicon?: LogoFavicon): void {
    // This method has document access → only run in browser
    if (!isPlatformBrowser(this.platformId)) return;

    if (logofavicon && (logofavicon.seasonal_desktop_logo || logofavicon.desktop_logo)) {
      this.desktop_logo$.next((logofavicon.seasonal_desktop_logo || logofavicon.desktop_logo).toString());
    } else {
      let defaultLogo = getComputedStyle(document.documentElement)
        .getPropertyValue('--topbar-logo-image')
        .trim();

      // Remove url("...") wrapper
      if (defaultLogo.startsWith('url("')) {
        defaultLogo = defaultLogo.slice(5, -2);
      } else if (defaultLogo.startsWith('url(')) {
        defaultLogo = defaultLogo.slice(4, -1).replace(/"/g, '');
      }

      // Clean up escapes
      defaultLogo = defaultLogo.replace(/\\/g, '');

      this.desktop_logo$.next(defaultLogo || '/assets/logo/default-logo.png');
    }
  }

  // New method: Always safe to call (used in SSR)
  private setLogoObservable(logofavicon?: LogoFavicon): void {
    if (logofavicon && (logofavicon.seasonal_desktop_logo || logofavicon.desktop_logo)) {
      this.desktop_logo$.next((logofavicon.seasonal_desktop_logo || logofavicon.desktop_logo).toString());
    } else {
      // Fallback: emit empty or default asset path (won't break SSR)
      this.desktop_logo$.next('/assets/logo/default-logo.png'); // or just ''
    }
  }

  clearLogoFavicon(): void {
    this.desktop_logo$.next('');
  }
}